import React, { useState, useEffect } from 'react';
import { useParams } from "react-router-dom";
import CalendarIcone from "../../../assets/icons/calendar.png";
import { FileDownload, Print } from "@mui/icons-material";
import { getArticleBySlug } from "../../../services/article.service";

const ArticleHeaderDetails = () => {
    const { id } = useParams();
    const [article, setArticle] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    const REACT_APP_BASE_URL = process.env.REACT_APP_BASE_URL;

    // Fonction pour formater les rubriques en chaîne de caractères
    const formatRubriques = (rubriques) => {
        if (!rubriques || !Array.isArray(rubriques) || rubriques.length === 0) {
            return '';
        }

        return rubriques.map(rubrique => rubrique.name_rub).join(', ');
    };

    const getImagePath = (photoPath) => {
        if (!photoPath) return '/placeholder-image.jpg'; // Placeholder si aucune image n'est disponible
        return photoPath.startsWith('http') ? photoPath : `${REACT_APP_BASE_URL}/${photoPath}`;
    };

    const getFilePath = (filePath) => {
        if (!filePath) return null; // Si aucun fichier n'est disponible
        return filePath.startsWith('http') ? filePath : `${REACT_APP_BASE_URL}/${filePath}`;
    };

    const handleDownload = () => {
        const fileUrl = getFilePath(article.file_art);
        if (fileUrl) {
            const link = document.createElement('a');
            link.href = fileUrl;
            link.setAttribute('download', fileUrl.split('/').pop());
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } else {
            alert("Fichier non disponible pour le téléchargement.");
        }
    };

    const handlePrint = () => {
        const fileUrl = getFilePath(article.file_art);
        if (fileUrl) {
            window.open(fileUrl, '_blank', 'noopener,noreferrer');
        } else {
            alert("Fichier non disponible pour l'impression.");
        }
    };

    useEffect(() => {
        const fetchArticle = async () => {
            try {
                setLoading(true);
                const data = await getArticleBySlug(id); // Appel à l'API avec le slug
                if (data && data.data) {
                    setArticle(data.data); // Accéder aux données réelles
                } else {
                    setError(true);
                }
            } catch (err) {
                console.error("Erreur lors de la récupération de l'article :", err);
                setError(true);
            } finally {
                setLoading(false);
            }
        };

        fetchArticle();
    }, [id]);

    // Gestion du cas où l'article n'est pas trouvé ou en cours de chargement
    if (loading) {
        return (
            <div className="text-center py-10">
                <h1 className="text-3xl font-bold text-greenprimary">Chargement...</h1>
            </div>
        );
    }

    if (error || !article) {
        return (
            <div className="text-center py-10">
                <h1 className="text-3xl font-bold text-red-600">Article non trouvé</h1>
                <p className="text-gray-500 mt-4">L'article que vous recherchez n'existe pas.</p>
            </div>
        );
    }

    // Gestion du formatage de la date
    const formatDate = (dateString) => {
        if (!dateString) return "Date non disponible";
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString('fr-FR', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
            });
        } catch (error) {
            console.error("Erreur de formatage de la date :", error);
            return "Date non valide";
        }
    };

    // Déterminer les rubriques à afficher
    const rubriquesDisplay = article.rubriques && Array.isArray(article.rubriques)
        ? formatRubriques(article.rubriques)
        : article.name_rub || "Rubrique non disponible";

    // Déterminer la première rubrique pour le fil d'Ariane
    const firstRubrique = article.rubriques && Array.isArray(article.rubriques) && article.rubriques.length > 0
        ? article.rubriques[0].name_rub
        : article.name_rub || "Rubrique non disponible";

    return (
        <div className="relative h-[320px] sm:h-[380px] md:h-[448px]">
            {/* Image de fond */}
            <img
                src={getImagePath(article.photo_art)} // Appel à la fonction getImagePath
                alt={article.title_art?.fr || "Titre non disponible"}
                className="absolute inset-0 w-full h-full object-cover z-0 filter brightness-50"
            />
            {/* Overlay noir */}
            <div className="absolute inset-0 bg-black opacity-20 z-10"></div>
            {/* Contenu textuel */}
            <div className="relative z-20 p-4 sm:p-6 pt-5 sm:pt-20 md:pt-28 max-w-7xl mx-auto">
                <p className="text-white bg-danger text-xs sm:text-sm font-semibold w-fit mb-2 sm:mb-4 px-2 sm:px-3 py-1">
                    {rubriquesDisplay}
                </p>
                <p className="text-white text-sm sm:text-base font-light mb-2">
                    ATLAS &gt; {firstRubrique}
                </p>
                <h1 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold mb-3 sm:mb-4 text-white max-w-5xl">
                    {article.title_art?.fr || "Titre non disponible"}
                </h1>
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center text-white mb-2 sm:mb-4">
                    <div className="flex items-center mb-2 sm:mb-0 text-sm sm:text-base">
                        <span>par {article.author?.first_name || "Auteur"} {article.author?.last_name || ""}</span>
                        <span className="mx-2"><img src={CalendarIcone} alt="calendar icone" className="w-3 sm:w-4 h-3 sm:h-4" /></span>
                        <span>{formatDate(article.date_pub_art)}</span>
                    </div>
                    <div className="flex items-center mt-1 sm:mt-0">
                        <button
                            onClick={handleDownload}
                            className="flex items-center mx-2 text-white hover:text-gray-300"
                        >
                            <FileDownload className="w-5 h-5" />
                        </button>
                        <button
                            onClick={handlePrint}
                            className="flex items-center mx-2 text-white hover:text-gray-300"
                        >
                            <Print className="w-5 h-5" />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ArticleHeaderDetails;